import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  startScreening,
  completeScreening,
  getScreeningStatus,
  getNextQuestion
} from "../../services/aiInterviewService";
import CandidateHrVoiceScreeningModal from "../../components/CandidateHrVoiceScreeningModal";

// Visual Icons
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const BotIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const Spinner = ({ className = "w-8 h-8" }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

// Dynamic Voice Soundwave Visualizer Bars
const VoiceSoundwave = ({ isActive, color = "amber" }) => {
  const colorMap = {
    amber: "bg-amber-400",
    emerald: "bg-emerald-400",
    indigo: "bg-indigo-400"
  };
  const barClass = colorMap[color] || colorMap.amber;

  return (
    <div className="flex items-center justify-center gap-1.5 h-12 my-2">
      {[40, 75, 100, 60, 90, 50, 85, 45, 95, 65, 80, 55].map((heightPct, idx) => (
        <div
          key={idx}
          className={`w-1.5 rounded-full transition-all duration-200 ${barClass} ${
            isActive ? "animate-pulse" : "opacity-30 h-2"
          }`}
          style={{
            height: isActive ? `${Math.max(12, heightPct)}%` : "8px",
            animationDelay: `${idx * 0.08}s`,
            animationDuration: `${0.6 + (idx % 4) * 0.2}s`
          }}
        />
      ))}
    </div>
  );
};

const VoiceInterview = ({ applicationId: propApplicationId, applicantData: propApplicant, onClose, onCompleted }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  // Resolve application ID
  const effectiveAppId =
    propApplicationId ||
    params.id ||
    params.applicationId ||
    searchParams.get("application_id") ||
    searchParams.get("applicationId") ||
    searchParams.get("applicant_id") ||
    location.state?.applicationId ||
    location.state?.applicant?.id ||
    null;

  // Voice Interaction State Machine:
  // idle -> starting -> ai_speaking -> listening -> processing_answer -> generating_next_question -> ai_speaking ... -> completed | expired
  const [status, setStatus] = useState("idle");
  const [applicant, setApplicant] = useState(propApplicant || location.state?.applicant || null);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [maxQuestions, setMaxQuestions] = useState(5);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [error, setError] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [showHrVoiceModal, setShowHrVoiceModal] = useState(false);

  // Refs for speech and silence detection
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const currentAnswerRef = useRef("");
  const lastSpeechTimeRef = useRef(Date.now());
  const hasSpokenRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const maxSilenceTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const isSpeechEndedRef = useRef(false);

  // Fetch applicant & deadline info
  useEffect(() => {
    if (effectiveAppId) {
      getScreeningStatus(effectiveAppId)
        .then((data) => {
          if (data) {
            setApplicant(data);
            if (typeof data.remaining_seconds === "number") {
              setRemainingSeconds(data.remaining_seconds);
            }
            if (data.is_expired || data.ai_screening_status === "EXPIRED") {
              setIsExpired(true);
              setStatus("expired");
            }
          }
        })
        .catch((err) => {
          console.log("Status fetch notice:", err.message);
        });
    }
  }, [effectiveAppId]);

  // Live countdown timer for 24-hour deadline
  useEffect(() => {
    if (remainingSeconds === null) return;
    countdownTimerRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev === null || prev <= 0) {
          clearInterval(countdownTimerRef.current);
          setIsExpired(true);
          setStatus("expired");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, [remainingSeconds]);

  // Auto-transition to HR Voice Screening if AI Screening passed
  useEffect(() => {
    if (status === 'completed' && evaluation) {
      const overallScore = typeof evaluation?.overall === 'number'
        ? evaluation.overall
        : (evaluation?.overall !== undefined && evaluation?.overall !== null ? Number(evaluation.overall) : 0);
      const recommendation = evaluation?.recommendation || (overallScore >= 60 ? 'Hire' : 'No Hire');
      const isPassed = overallScore >= 60 && recommendation !== 'No Hire' && recommendation !== 'Fail';

      if (isPassed && !showHrVoiceModal) {
        const timer = setTimeout(() => {
          setShowHrVoiceModal(true);
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [status, evaluation, showHrVoiceModal]);

  const formatCountdown = (totalSecs) => {
    if (!totalSecs || totalSecs <= 0) return "00:00:00";
    const hours = Math.floor(totalSecs / 3600);
    const minutes = Math.floor((totalSecs % 3600) / 60);
    const seconds = totalSecs % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const cleanPosition = (pos) => {
    if (!pos) return "Role";
    return String(pos).replace(/\s*Interview\s*$/i, "").trim();
  };

  const handleTakeItLater = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/job-marketplace');
    }
  };

  const handleCandidateFinishedSpeakingRef = useRef(null);

  // -------------------------------------------------------------
  // Speech-To-Text (STT): Teardown Microphone & Timers
  // -------------------------------------------------------------
  const stopListening = useCallback(() => {
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (maxSilenceTimerRef.current) {
      clearTimeout(maxSilenceTimerRef.current);
      maxSilenceTimerRef.current = null;
    }
    isListeningRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onstart = null;
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
      } catch (e) {}
      recognitionRef.current = null;
    }
  }, []);

  // -------------------------------------------------------------
  // Text-To-Speech (TTS): AI Speaks (Strict Turn Isolation)
  // -------------------------------------------------------------
  const speakAI = useCallback((text) => {
    return new Promise((resolve) => {
      // 1. CRITICAL: Turn OFF microphone and reset transcripts before TTS starts speaking
      stopListening();
      setLiveTranscript("");
      setStatus("ai_speaking");

      if (!window.speechSynthesis) return resolve();

      try {
        window.speechSynthesis.cancel();
        window.speechSynthesis.resume();
      } catch (e) {}

      let isResolved = false;
      const safeResolve = () => {
        if (!isResolved) {
          isResolved = true;
          if (timeoutId) clearTimeout(timeoutId);
          // 400ms audio buffer to allow device speaker echo to dissipate before opening mic
          setTimeout(() => {
            resolve();
          }, 400);
        }
      };

      // Realistic timeout based on character length (80ms/char, min 6s, max 30s)
      const calculatedMs = Math.max(6000, (text || "").length * 90);
      const maxMs = Math.min(30000, calculatedMs);

      const timeoutId = setTimeout(() => {
        safeResolve();
      }, maxMs);

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.0;
      utterance.lang = "en-US";

      const voices = window.speechSynthesis.getVoices();
      const naturalVoice = voices.find(
        (v) => (v.name.includes("Natural") || v.name.includes("Google") || v.name.includes("Samantha") || v.name.includes("Zira")) && v.lang.startsWith("en")
      );
      if (naturalVoice) {
        utterance.voice = naturalVoice;
      }

      utterance.onend = () => {
        safeResolve();
      };
      utterance.onerror = (e) => {
        console.warn("Speech synthesis notice:", e);
        safeResolve();
      };

      try {
        window.speechSynthesis.speak(utterance);
      } catch (e) {
        safeResolve();
      }
    });
  }, [stopListening]);

  // -------------------------------------------------------------
  // Speech-To-Text (STT): Candidate Voice Capture with Silence Detection
  // -------------------------------------------------------------
  const startContinuousListening = useCallback(() => {
    // Safety check: ensure TTS is not currently speaking before opening mic
    if (window.speechSynthesis && window.speechSynthesis.speaking) {
      setTimeout(() => {
        startContinuousListening();
      }, 350);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.");
      return;
    }

    // Clean teardown any active listener or timers
    stopListening();

    // Reset voice capture states
    currentAnswerRef.current = "";
    setLiveTranscript("");
    hasSpokenRef.current = false;
    lastSpeechTimeRef.current = Date.now();
    isSpeechEndedRef.current = false;
    isListeningRef.current = true;
    setStatus("listening");

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.interimResults = true;
      recognition.continuous = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        if (window.speechSynthesis && window.speechSynthesis.speaking) {
          try { recognition.abort(); } catch (e) {}
          return;
        }
        isListeningRef.current = true;
        lastSpeechTimeRef.current = Date.now();
      };

      recognition.onresult = (event) => {
        // Reject audio if TTS engine is still speaking
        if (window.speechSynthesis && window.speechSynthesis.speaking) {
          return;
        }

        let interimText = "";
        let finalText = "";

        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalText += result[0].transcript + " ";
          } else {
            interimText += result[0].transcript;
          }
        }

        const combined = (finalText + interimText).trim();
        if (combined.length > 0) {
          currentAnswerRef.current = combined;
          setLiveTranscript(combined);
          hasSpokenRef.current = true;
          lastSpeechTimeRef.current = Date.now();
        }
      };

      recognition.onerror = (event) => {
        if (event.error === "no-speech") {
          return;
        }
        if (event.error === "not-allowed") {
          setError("Microphone permission was denied. Please allow microphone access to participate in the AI voice interview.");
          stopListening();
          setStatus("idle");
          return;
        }
        console.warn("Speech recognition notice:", event.error);
      };

      recognition.onend = () => {
        // Instance Guard: Ignore callbacks from previous stale instances
        if (recognitionRef.current !== recognition) return;

        // If still in listening state and user was speaking, restart recognition unless stopped
        if (isListeningRef.current && !isSpeechEndedRef.current) {
          try {
            recognition.start();
          } catch (e) {}
        }
      };

      recognitionRef.current = recognition;
      recognition.start();

      // Start Silence Detector: triggers automatic progression when candidate pauses for 3.0 seconds after speaking
      silenceTimerRef.current = setInterval(() => {
        const elapsedSinceSpeech = Date.now() - lastSpeechTimeRef.current;
        const capturedText = currentAnswerRef.current.trim();

        // If words detected (>= 3 characters) and candidate has been silent for 3.0 seconds -> auto advance!
        if (hasSpokenRef.current && capturedText.length >= 3 && elapsedSinceSpeech >= 3000) {
          if (!isSpeechEndedRef.current) {
            isSpeechEndedRef.current = true;
            stopListening();
            if (handleCandidateFinishedSpeakingRef.current) {
              handleCandidateFinishedSpeakingRef.current(capturedText);
            }
          }
        }
      }, 250);

      // Max Silence Guard: 15s if silent from start, 30s max if speaking continuously
      maxSilenceTimerRef.current = setTimeout(() => {
        if (isListeningRef.current && !isSpeechEndedRef.current) {
          isSpeechEndedRef.current = true;
          const captured = currentAnswerRef.current.trim() || "[No response / Silent]";
          stopListening();
          if (handleCandidateFinishedSpeakingRef.current) {
            handleCandidateFinishedSpeakingRef.current(captured);
          }
        }
      }, hasSpokenRef.current ? 30000 : 15000);

    } catch (err) {
      console.error("Failed to start speech recognition:", err);
      setError("Microphone initialization error. Please check your browser audio permissions.");
      setStatus("idle");
    }
  }, [stopListening]);

  // -------------------------------------------------------------
  // Conversational Progression Cycle
  // -------------------------------------------------------------
  const handleCandidateFinishedSpeaking = useCallback(async (candidateSpokenAnswer) => {
    stopListening();
    setStatus("processing_answer");

    const newHistory = [
      ...conversationHistory,
      {
        question: currentQuestion,
        answer: candidateSpokenAnswer
      }
    ];
    setConversationHistory(newHistory);

    const nextQIndex = questionIndex + 1;
    setQuestionIndex(nextQIndex);

    // Call backend for dynamic follow-up question
    setStatus("generating_next_question");
    try {
      let nextData = null;
      if (effectiveAppId) {
        nextData = await getNextQuestion({
          applicationId: effectiveAppId,
          history: newHistory,
          questionIndex: nextQIndex,
          maxQuestions
        });
      }

      // Check if interview reached completion
      if (nextData?.is_completed || nextQIndex >= maxQuestions) {
        await handleInterviewComplete(newHistory, nextData?.closing_speech);
      } else {
        let nextQText = nextData?.question;
        const askedSet = new Set(newHistory.map((h) => h.question?.trim().toLowerCase()));

        if (!nextQText || askedSet.has(nextQText.trim().toLowerCase())) {
          const fallbackQuestions = [
            "Could you elaborate on how you apply your core technical skills to solve complex problems in a team environment?",
            "Can you walk me through a challenging technical bug or performance issue you diagnosed and resolved?",
            "How do you evaluate code quality, automated testing, and security before deploying to production?",
            "What technical strengths make you confident you can make an immediate positive impact in this position?"
          ];
          const unusedFallback = fallbackQuestions.find((fq) => !askedSet.has(fq.toLowerCase()));
          nextQText = unusedFallback || fallbackQuestions[(nextQIndex - 1) % fallbackQuestions.length];
        }

        setCurrentQuestion(nextQText);
        await speakAI(nextQText);
        startContinuousListening();
      }
    } catch (err) {
      console.error("Error generating next question:", err);
      if (nextQIndex < maxQuestions) {
        const fallbackQuestions = [
          "Could you elaborate on how you apply your core technical skills to solve complex problems in a team environment?",
          "Can you walk me through a challenging technical bug or performance issue you diagnosed and resolved?",
          "How do you evaluate code quality, automated testing, and security before deploying to production?",
          "What technical strengths make you confident you can make an immediate positive impact in this position?"
        ];
        const fallbackQ = fallbackQuestions[(nextQIndex - 1) % fallbackQuestions.length];
        setCurrentQuestion(fallbackQ);
        await speakAI(fallbackQ);
        startContinuousListening();
      } else {
        await handleInterviewComplete(newHistory);
      }
    }
  }, [conversationHistory, currentQuestion, effectiveAppId, maxQuestions, questionIndex, speakAI, startContinuousListening, stopListening]);

  handleCandidateFinishedSpeakingRef.current = handleCandidateFinishedSpeaking;

  // -------------------------------------------------------------
  // Final Interview Completion & Evaluation
  // -------------------------------------------------------------
  const handleInterviewComplete = async (finalHistory, closingSpeech) => {
    setStatus("ai_speaking");
    const candidateName = applicant?.applicant_name || "Candidate";
    const speech = closingSpeech || `Thank you, ${candidateName}. That completes your AI screening interview. Your responses have been recorded and evaluated. You may now close this screen.`;
    
    await speakAI(speech);
    setStatus("evaluating");

    try {
      const completeRes = await completeScreening({
        applicationId: effectiveAppId,
        transcript: finalHistory,
        answers: finalHistory,
        candidateId: applicant?.candidate_id || undefined
      });

      setEvaluation(completeRes?.evaluation || {
        overall: completeRes?.ai_screening_score || 82,
        recommendation: completeRes?.ai_screening_result || "PASS",
        summary: "Candidate demonstrated strong communication, technical competence, and practical problem solving in the voice screening interview."
      });

      setStatus("completed");
      if (onCompleted) {
        onCompleted(completeRes);
      }
    } catch (err) {
      console.error("Failed to complete screening:", err);
      setError("AI evaluation finished. Your results have been submitted to the recruitment team.");
      setStatus("completed");
    }
  };

  // -------------------------------------------------------------
  // Start AI Voice Interview (Initiated by candidate click)
  // -------------------------------------------------------------
  const startInterview = async () => {
    if (isExpired) {
      setStatus("expired");
      return;
    }

    setError(null);
    setStatus("starting");

    try {
      // 1. Request microphone permission explicitly
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      // 2. Start screening session on backend
      let initialQuestion = "";
      if (effectiveAppId) {
        const sessionData = await startScreening({ applicationId: effectiveAppId });
        if (sessionData?.status === "expired" || sessionData?.is_expired) {
          setIsExpired(true);
          setStatus("expired");
          return;
        }
        if (sessionData?.position) {
          setApplicant((prev) => ({ ...prev, ...sessionData }));
        }
        initialQuestion = sessionData?.questions?.[0] || "";
      }

      const candidateName = applicant?.applicant_name || "Candidate";
      const rawRole = applicant?.position || applicant?.job_title || "Role";
      const jobPosition = cleanPosition(rawRole);

      if (!initialQuestion) {
        initialQuestion = `Hello ${candidateName}. Welcome to your AI screening interview for the ${jobPosition} position. I have reviewed your resume. To get started, please introduce yourself and tell me about your background and key technical projects.`;
      }

      setCurrentQuestion(initialQuestion);
      setQuestionIndex(0);
      setConversationHistory([]);

      // 3. AI Speaks Question 1 out loud
      await speakAI(initialQuestion);

      // 4. Automatically open microphone and start listening
      startContinuousListening();
    } catch (err) {
      console.error("Failed to start voice interview:", err);
      if (err.name === "NotAllowedError" || err.message?.includes("permission")) {
        setError("Microphone permission is required for the voice interview. Please click allow when prompted by your browser.");
      } else if (err.message && err.message.toLowerCase().includes("expired")) {
        setIsExpired(true);
        setStatus("expired");
      } else {
        setError(err.message || "Failed to initialize AI voice interview.");
      }
      setStatus("idle");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [stopListening]);

  const progressPct = Math.round((Math.min(questionIndex + 1, maxQuestions) / maxQuestions) * 100);
  const displayPosition = cleanPosition(applicant?.position || applicant?.job_title || "");

  return (
    <div className="min-h-full bg-slate-950 text-slate-100 px-4 sm:px-6 py-8 rounded-2xl shadow-2xl relative overflow-hidden border border-slate-800">
      {/* Close button if inside modal */}
      {onClose && (
        <button
          onClick={() => {
            stopListening();
            if (window.speechSynthesis) window.speechSynthesis.cancel();
            onClose();
          }}
          className="absolute top-4 right-4 text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800/80 transition-colors z-20"
          title="Close screening"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      <div className="max-w-2xl mx-auto">
        {/* Header Badge */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 text-xs font-semibold mb-2">
            <span className="w-2 h-2 rounded-full bg-indigo-400 animate-ping" />
            <span>AI Voice Screening & Evaluation</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
            {displayPosition ? `${displayPosition} Interview` : "Voice-to-Voice AI Interview"}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-1">
            {applicant?.applicant_name ? `Candidate: ${applicant.applicant_name}` : "Interactive spoken interview with real-time speech AI"}
          </p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 p-4 text-xs sm:text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200 ml-4 font-bold">×</button>
          </div>
        )}

        {/* -------------------------------------------------------------
            SCREEN 1: IDLE / START INTERVIEW
           ------------------------------------------------------------- */}
        {status === "idle" && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-800 bg-slate-900/80 p-8 sm:p-10 text-center space-y-6">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-amber-400/10 border border-amber-400/30 flex items-center justify-center shadow-inner">
                <MicIcon className="w-10 h-10 text-amber-400" />
              </div>
              <span className="absolute -bottom-1 -right-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-4 w-4 bg-amber-500" />
              </span>
            </div>

            <div className="space-y-2 max-w-md">
              <h2 className="text-xl font-bold text-white">Real-Time Voice AI Interview</h2>
              <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                Your AI interviewer will conduct a natural voice-based interview tailored to your resume and the job requirements.
                You will answer all questions using your microphone. <strong>No typing is required.</strong>
              </p>
            </div>

            {/* 24-Hour Countdown Box */}
            {remainingSeconds !== null && (
              <div className="w-full max-w-md bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-amber-500/15 border border-amber-500/30 rounded-xl p-4 text-center space-y-1">
                <p className="text-xs font-bold text-amber-400 uppercase tracking-wider flex items-center justify-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>24h Screening Deadline Active</span>
                </p>
                <div className="text-3xl font-extrabold text-amber-300 font-mono tracking-widest">
                  {formatCountdown(remainingSeconds)}
                </div>
                <p className="text-[11px] text-amber-200/70">Time Remaining to Complete</p>
              </div>
            )}

            <div className="flex flex-col items-center gap-3 w-full max-w-xs">
              <button
                onClick={startInterview}
                className="w-full rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 hover:from-amber-300 hover:to-orange-400 transition-all transform hover:scale-105 text-slate-950 font-bold px-8 py-3.5 text-sm shadow-xl flex items-center justify-center gap-2.5 cursor-pointer"
              >
                <MicIcon className="w-5 h-5" />
                <span>START AI INTERVIEW</span>
              </button>

              <button
                type="button"
                onClick={handleTakeItLater}
                className="w-full rounded-xl border border-slate-700 hover:border-slate-600 bg-slate-800/40 hover:bg-slate-800 text-slate-300 hover:text-white font-semibold px-6 py-2.5 text-xs sm:text-sm transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
              >
                <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Take It Later</span>
              </button>
            </div>

            <p className="text-[11px] text-slate-500">
              Microphone permission will be requested upon clicking start.
            </p>
          </div>
        )}

        {/* -------------------------------------------------------------
            SCREEN 2: DEADLINE EXPIRED
           ------------------------------------------------------------- */}
        {status === "expired" && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-red-500/30 bg-slate-900/90 p-8 sm:p-10 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white">AI Screening Deadline Expired</h2>
            <p className="text-slate-400 text-xs sm:text-sm max-w-md leading-relaxed">
              Your 24-hour screening window for this position has passed. You are no longer able to complete this AI voice screening.
            </p>
            {onClose && (
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2.5 rounded-lg border border-slate-700 bg-slate-800 text-slate-200 text-xs sm:text-sm hover:bg-slate-700"
              >
                Close
              </button>
            )}
          </div>
        )}

        {/* -------------------------------------------------------------
            SCREEN 3: STARTING LOADER
           ------------------------------------------------------------- */}
        {status === "starting" && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-800 bg-slate-900/80 p-12 text-center space-y-4">
            <Spinner className="w-10 h-10 text-amber-400" />
            <h3 className="text-base font-semibold text-white">Preparing Your Personalized Voice Interview...</h3>
            <p className="text-slate-400 text-xs">Analyzing candidate resume & job description with AI recruiter.</p>
          </div>
        )}

        {/* -------------------------------------------------------------
            SCREEN 4: ACTIVE VOICE-TO-VOICE CONVERSATION
           ------------------------------------------------------------- */}
        {(status === "ai_speaking" ||
          status === "listening" ||
          status === "processing_answer" ||
          status === "generating_next_question") && (
          <div className="rounded-2xl border border-slate-800 bg-slate-900/90 overflow-hidden shadow-2xl">
            {/* Progress Bar */}
            <div className="h-1.5 bg-slate-800">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-all duration-500 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            <div className="p-6 sm:p-8 space-y-6">
              {/* Question Index & Live State Badge */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                  Question {questionIndex + 1} of {maxQuestions}
                </span>

                {status === "ai_speaking" && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-semibold animate-pulse">
                    <BotIcon className="w-3.5 h-3.5" />
                    <span>AI IS SPEAKING...</span>
                  </span>
                )}

                {status === "listening" && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-bold animate-pulse">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    <span>LISTENING... (SPEAK NOW)</span>
                  </span>
                )}

                {(status === "processing_answer" || status === "generating_next_question") && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 text-xs font-semibold">
                    <Spinner className="w-3.5 h-3.5 text-indigo-400" />
                    <span>ANALYZING RESPONSE...</span>
                  </span>
                )}
              </div>

              {/* Spoken AI Question Display */}
              <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-5 text-center min-h-[90px] flex items-center justify-center">
                <p className="text-base sm:text-lg font-medium text-slate-100 leading-relaxed">
                  "{currentQuestion}"
                </p>
              </div>

              {/* Dynamic Soundwave Visualizer & Status Orb */}
              <div className="flex flex-col items-center justify-center py-4 space-y-4">
                {status === "ai_speaking" && (
                  <>
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400/20 to-orange-500/20 border-2 border-amber-400 flex items-center justify-center shadow-lg shadow-amber-500/20 animate-pulse">
                      <BotIcon className="w-10 h-10 text-amber-400" />
                    </div>
                    <VoiceSoundwave isActive={true} color="amber" />
                    <p className="text-xs text-amber-300 font-medium">Please listen to the AI interviewer...</p>
                  </>
                )}

                {status === "listening" && (
                  <>
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-400 flex items-center justify-center shadow-lg shadow-emerald-500/30 animate-pulse">
                        <MicIcon className="w-10 h-10 text-emerald-400" />
                      </div>
                      <span className="absolute -top-1 -right-1 flex h-4 w-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500" />
                      </span>
                    </div>
                    <VoiceSoundwave isActive={true} color="emerald" />
                    <p className="text-xs text-emerald-300 font-medium">
                      Speak naturally into your microphone. Pause when finished.
                    </p>
                  </>
                )}

                {(status === "processing_answer" || status === "generating_next_question") && (
                  <>
                    <div className="w-20 h-20 rounded-full bg-indigo-500/20 border-2 border-indigo-400/50 flex items-center justify-center shadow-lg">
                      <Spinner className="w-9 h-9 text-indigo-400" />
                    </div>
                    <VoiceSoundwave isActive={false} color="indigo" />
                    <p className="text-xs text-indigo-300 font-medium">
                      Evaluating your answer & generating follow-up question...
                    </p>
                  </>
                )}
              </div>

              {/* Live Spoken Captions Stream (Read-only transcript) */}
              {liveTranscript && (
                <div className="bg-slate-950/80 border border-slate-800/80 rounded-xl p-4">
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span>Your Spoken Voice (Live Transcript):</span>
                  </p>
                  <p className="text-xs sm:text-sm text-slate-200 italic leading-relaxed">
                    "{liveTranscript}"
                  </p>
                </div>
              )}

              {/* Manual & Auto Progression Controls */}
              {status === "listening" && (
                <div className="flex flex-col items-center pt-2 space-y-2">
                  <button
                    onClick={() => {
                      if (!isSpeechEndedRef.current) {
                        isSpeechEndedRef.current = true;
                        const text = currentAnswerRef.current.trim() || "Candidate submitted spoken answer.";
                        stopListening();
                        handleCandidateFinishedSpeaking(text);
                      }
                    }}
                    className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-6 py-2.5 text-xs sm:text-sm shadow-lg flex items-center gap-2 transition-all transform hover:scale-105 cursor-pointer"
                  >
                    <span>Submit Answer & Next Question</span>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                  <p className="text-[11px] text-slate-400">
                    💡 The AI automatically advances after you stop speaking for 2 seconds, or click above to submit manually.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* -------------------------------------------------------------
            SCREEN 5: EVALUATING LOADER
           ------------------------------------------------------------- */}
        {status === "evaluating" && (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-800 bg-slate-900/90 p-12 text-center space-y-4">
            <Spinner className="w-12 h-12 text-emerald-400" />
            <h3 className="text-lg font-bold text-white">Evaluating Interview Responses...</h3>
            <p className="text-slate-400 text-xs max-w-sm">
              Calculating multi-competency scores across Technical, Communication, Problem Solving, and Role Fit.
            </p>
          </div>
        )}

        {/* -------------------------------------------------------------
            SCREEN 6: INTERVIEW COMPLETED & FINAL EVALUATION
           ------------------------------------------------------------- */}
        {status === "completed" && (() => {
          const overallScore = typeof evaluation?.overall === 'number'
            ? evaluation.overall
            : (evaluation?.overall !== undefined && evaluation?.overall !== null ? Number(evaluation.overall) : 0);
          const recommendation = evaluation?.recommendation || (overallScore >= 60 ? 'Hire' : 'No Hire');
          const isPassed = overallScore >= 60 && recommendation !== 'No Hire' && recommendation !== 'Fail';

          return (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/90 overflow-hidden shadow-2xl p-6 sm:p-8 space-y-6">
              <div className="text-center space-y-2">
                <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${
                  isPassed ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-red-500/20 text-red-400 border border-red-500/40"
                }`}>
                  <span className="text-2xl font-extrabold">{overallScore}%</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  {isPassed ? "AI Screening Completed — Passed" : "AI Screening Completed — Failed"}
                </h2>
                <p className="text-xs sm:text-sm text-slate-400 max-w-md mx-auto">
                  {isPassed
                    ? "Your voice responses met the screening benchmark. You can now take the HR Screening voice interview."
                    : "Your voice screening interview did not meet the required benchmark. Results have been recorded."}
                </p>
              </div>

              {/* Competency Scores */}
              {evaluation?.scores && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {Object.entries(evaluation.scores).map(([key, val]) => (
                    <div key={key} className="bg-slate-800/60 border border-slate-700/80 rounded-xl p-3 text-center">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400 capitalize">{key}</p>
                      <p className={`text-lg font-extrabold mt-0.5 ${val >= 60 ? 'text-white' : 'text-red-400'}`}>
                        {val ?? 0}%
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary */}
              {evaluation?.summary && (
                <div className="bg-slate-800/40 border border-slate-700/70 rounded-xl p-4">
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Evaluation Summary</p>
                  <p className="text-xs sm:text-sm text-slate-200 leading-relaxed">{evaluation.summary}</p>
                </div>
              )}

              <div className="pt-2 flex flex-col items-center gap-3">
                <div className={`w-full border rounded-xl p-4 text-left space-y-1 ${
                  isPassed
                    ? 'bg-gradient-to-r from-blue-900/50 to-indigo-900/50 border-blue-500/40'
                    : 'bg-gradient-to-r from-slate-900/80 to-amber-900/30 border-amber-500/40'
                }`}>
                  <p className={`text-xs uppercase tracking-wider font-bold ${isPassed ? 'text-blue-400' : 'text-amber-400'}`}>
                    Continuous Interview Flow
                  </p>
                  <p className="text-xs text-slate-200">
                    {isPassed
                      ? "Great! You passed technical screening. Click below to continue directly into your 7-question HR Voice Screening."
                      : "Even though your AI technical score was below benchmark, you may still complete the HR Voice Screening. HR will review both evaluation results."}
                  </p>
                </div>
                <button
                  onClick={() => setShowHrVoiceModal(true)}
                  className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold px-8 py-3.5 text-sm shadow-xl flex items-center justify-center gap-2 cursor-pointer transition-transform hover:scale-[1.01]"
                >
                  <MicIcon className="w-5 h-5" />
                  <span>TAKE HR SCREENING (VOICE INTERVIEW)</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>

              {/* HR Voice Screening Modal Overlay */}
              {showHrVoiceModal && (
                <CandidateHrVoiceScreeningModal
                  isOpen={showHrVoiceModal}
                  applicationId={effectiveAppId}
                  initialData={applicant}
                  autoStart={true}
                  onClose={() => {
                    setShowHrVoiceModal(false);
                    if (onClose) onClose();
                    else navigate('/job-marketplace');
                  }}
                  onCompleted={() => {
                    setShowHrVoiceModal(false);
                    if (onClose) onClose();
                    else navigate('/job-marketplace');
                  }}
                />
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default VoiceInterview;